<template>
    <div>
        <!-- Alerts (MONEY_TIME_TARGET uniquement) -->
        <transition name="slide-fade">
            <v-alert
                v-if="goalReachedEarly"
                type="success"
                color="teal darken-2"
                class="mt-2 mb-2 elevation-6"
                :value="true"
                dismissible
            >
                <v-layout align-center>
                    <v-icon dark large class="mr-3">emoji_events</v-icon>
                    <div>
                        <div class="subheading font-weight-bold">🎉 Amazing progress — you’re ahead of time!</div>
                        <div class="body-2 mt-1">
                            Incredible work — your campaign <strong>{{ campaign.name }}</strong> has already reached its financial goal of
                            <strong>{{ campaign.target_amount }} FCFA</strong>.
                            You still have until <strong>{{ campaign.target_date }}</strong> to keep building your savings habit.
                            Every extra contribution strengthens your financial discipline and future plans.
                        </div>
                    </div>
                </v-layout>
            </v-alert>
        </transition>

        <transition name="slide-fade">
            <v-alert
                v-if="durationReachedWithoutAmount"
                type="warning"
                color="orange darken-3"
                class="mt-2 mb-2 elevation-6"
                :value="true"
                dismissible
            >
                <v-layout align-center>
                    <v-icon dark large class="mr-3">schedule</v-icon>
                    <div>
                        <div class="subheading font-weight-bold">⏰ Time goal reached — keep pushing forward!</div>
                        <div class="body-2 mt-1">
                            Your campaign <strong>{{ campaign.name }}</strong> has reached its planned duration ending on
                            <strong>{{ campaign.target_date }}</strong>.
                            You are currently at <strong>{{ campaign.saved_amount }} FCFA</strong> out of
                            <strong>{{ campaign.target_amount }} FCFA</strong>.
                            You’re still progressing — every contribution brings you closer to your goal.
                        </div>
                    </div>
                </v-layout>
            </v-alert>
        </transition>

        <!-- Campaign Header -->
        <v-layout row wrap text-xs-center>
            <v-flex xs12>
                <h2 class="font-weight-bold text-xs-left"><u>Campaign: {{campaign.name}}</u></h2>
            </v-flex>

            <!-- ✅ Bouton Interrupt: TOUJOURS VISIBLE si Ongoing (aucune restriction) -->
            <v-flex xs6 class="text-xs-left" v-show="campaign.status == 'Ongoing'">
                <v-btn
                    @click="setInterruptCampaign()"
                    small
                    class="elevation-6 white--text ma-0 text-xs-center"
                    round
                    color="secondary lighten-3"
                >
                    <v-icon left small>cancel</v-icon>
                    Interrupt
                </v-btn>
            </v-flex>

            <!-- ✅ Save Now: toujours xs6 (Interrupt toujours présent) -->
            <v-flex class="xs6 text-xs-right" v-show="campaign.status == 'Ongoing'">
                <v-tooltip bottom v-if="totalRequests >= maxRequests">
                    <template v-slot:activator="{ on }">
                        <div v-on="on">
                            <v-btn
                                small
                                class="animate__animated animate__flash animate__delay-1s elevation-6 white--text ma-0 text-xs-center"
                                round
                                color="teal darken-2"
                                :to="'/savings/campaigns/save/' + campaign.id"
                                :disabled="totalRequests >= maxRequests"
                            >
                                <v-icon left small class="fas fa-piggy-bank"></v-icon>
                                Save Now
                            </v-btn>
                        </div>
                    </template>
                    <span>Max 3 Transactions attempts a day, will be activated tomorrow</span>
                </v-tooltip>
                <v-btn
                    v-else
                    small
                    class="animate__animated animate__flash animate__delay-1s elevation-6 white--text ma-0 text-xs-center"
                    round
                    color="teal darken-2"
                    :to="'/savings/campaigns/save/' + campaign.id"
                >
                    <v-icon left small class="fas fa-piggy-bank"></v-icon>
                    Save Now
                </v-btn>
            </v-flex>
        </v-layout>

        <!-- Campaign Info Cards -->
        <v-card class="primary darken-2 mt-1">
            <v-card-text v-if="campaign.target_amount && campaign.target_date" class="pa-2" style="font-size: 12px;">
                <span class="white--text text-xs-left subheading"><b>Amount Saved:</b>
                    <span class="pa-0 ma-1 dark--text text-xs-left body-2">{{campaign.saved_amount}} FCFA in {{campaign.passed_days}} days</span>
                </span>
            </v-card-text>
            <v-card-text v-else-if="campaign.target_amount" class="pa-2" style="font-size: 12px;">
                <span class="white--text text-xs-left subheading"><b>Amount Saved:</b>
                    <span class="pa-0 ma-1 dark--text text-xs-left body-2">{{campaign.saved_amount}} FCFA</span>
                </span>
            </v-card-text>
            <v-card-text v-else-if="campaign.target_date" class="pa-2" style="font-size: 12px;">
                <span class="white--text text-xs-left subheading"><b>Amount Saved:</b>
                    <span class="pa-0 ma-1 dark--text text-xs-left body-2">{{campaign.saved_amount}} FCFA</span>
                </span>
            </v-card-text>
        </v-card>

        <v-card class="white elevation-6">
            <v-card-text class="body-2 pa-2" style="font-size: 12px; padding: 5px">
                <span><b>Creation Date:</b> <span class="caption">{{campaign.get_date_str}}</span></span>
                <v-spacer></v-spacer>
                <div v-if="campaign.target_amount && campaign.target_date">
                    <span><b>Objective:</b> <span class="caption">Reach {{campaign.target_amount}} F CFA By the {{campaign.target_date}}</span></span>
                    <v-spacer></v-spacer>
                    <span><b>Remaining:</b> <span class="caption">{{campaign.remaining_days}} days left</span></span>
                </div>
                <div v-else-if="campaign.target_amount">
                    <span><b>Objective:</b> <span class="caption">Reach {{campaign.target_amount}} F CFA</span></span>
                    <v-spacer></v-spacer>
                    <span><b>Remaining:</b> <span class="caption">{{campaign.remaining_days}} F CFA</span></span>
                </div>
                <div v-else-if="campaign.target_date">
                    <span><b>Objective:</b> <span class="caption">On the {{campaign.target_date}}</span></span>
                    <v-spacer></v-spacer>
                    <span><b>Remaining:</b> <span class="caption">{{campaign.remaining_days}} days left</span></span>
                </div>
                <v-spacer></v-spacer>
                <span><b>Percentage Complete:</b> <span class="caption">{{campaign.percentage_completed}}</span></span>
                <v-spacer></v-spacer>
                <span><b>Description:</b> <span class="caption">{{campaign.description}}</span></span>
            </v-card-text>
        </v-card>

        <!-- Requested Transactions -->
        <h2 v-if="campaignRequestedTransactions.length !== 0" class="mt-3 dark--text font-weight-bold text-xs-left">
            <u>Requested Transactions</u>
        </h2>
        <div v-if="campaignRequestedTransactions.length !== 0" class="white elevation-6 pa-2" style="max-height: 50px; overflow: auto; border-radius: 7px">
            <div color="white" v-for="item in campaignRequestedTransactions" :key="item.id">
                <v-card-text class="pa-1" style="font-size: 12px;">
                    <v-layout row wrap v-if="!item.cancelled || item.withdrawal || (!item.withdrawal && !item.confirmed)">
                        <v-flex xs8 text-xs-left>
                            <div v-if="item.withdrawal">
                                <span class="red--text font-weight-bold">Interrupt:</span><span> {{item.amount}} FCFA</span>
                            </div>
                            <div v-else>
                                <span class="dark--text font-weight-bold">Deposit:</span><span> {{item.amount}} FCFA</span>
                            </div>
                            <div v-if="!item.confirmed && !item.cancelled">
                                <span class="font-weight-bold">Status:</span>
                                <span class="primary--text font-weight-bold"> Pending</span>
                            </div>
                            <div v-if="item.confirmed">
                                <span class="font-weight-bold">Status:</span>
                                <span class="success--text font-weight-bold"> Confirmed</span>
                            </div>
                        </v-flex>
                        <v-flex xs4 text-xs-right>
                            <span class="dark--text font-weight-bold">Date:</span><span> {{item.get_date_str}}</span>
                        </v-flex>
                    </v-layout>
                </v-card-text>
                <v-divider></v-divider>
            </div>
        </div>

        <!-- Validated Transactions -->
        <h2 class="mt-3 dark--text font-weight-bold text-xs-left"><u>Validated Transactions</u></h2>
        <div class="white elevation-6 pa-2" style="max-height: 300px; overflow: auto; border-radius: 7px">
            <div color="white" v-for="item in businessTransactions" :key="'b-'+item.id">
                <v-card-text class="pa-1" style="font-size: 12px;">
                    <v-layout row wrap>
                        <v-flex xs6 text-xs-left>
                            <span class="dark--text font-weight-bold">Business Deposit:</span>
                            <span> {{item.amount}} FCFA</span>
                        </v-flex>
                        <v-flex xs6 text-xs-right>
                            <span class="dark--text font-weight-bold">Date:</span>
                            <span> {{item.get_day_month_year_str}}</span>
                        </v-flex>
                        <v-flex xs8 text-xs-left>
                            <span class="dark--text font-weight-bold"> By:</span>
                            <span> {{item.processed_by}} (Business Suite)</span>
                        </v-flex>
                        <v-flex xs4 text-xs-right>
                            <span class="dark--text font-weight-bold">at</span>
                            <span> {{item.transaction_time}}</span>
                        </v-flex>
                    </v-layout>
                </v-card-text>
                <v-divider></v-divider>
            </div>
            <div color="white" v-for="item in campaignTransactions" :key="item.id">
                <v-card-text class="pa-1" style="font-size: 12px;">
                    <v-layout row wrap>
                        <v-flex xs6 text-xs-left>
                            <div v-if="item.withdrawal">
                                <span class="dark--text font-weight-bold">Transferred:</span><span> {{item.amount}} FCFA</span>
                            </div>
                            <div v-else>
                                <span class="dark--text font-weight-bold">Deposit:</span><span> {{item.amount}} FCFA</span>
                            </div>
                        </v-flex>
                        <v-flex xs6 text-xs-right>
                            <span class="dark--text font-weight-bold">Date:</span><span> {{item.get_day_month_year_str}}</span>
                        </v-flex>
                        <v-flex xs8 text-xs-left>
                            <span class="dark--text font-weight-bold">By:</span>
                            <span> {{item.top_up_admin_username}} - {{item.top_up_admin_phone_number}}</span>
                        </v-flex>
                        <v-flex xs4 text-xs-right>
                            <span class="dark--text font-weight-bold">at</span><span> {{item.transaction_time}}</span>
                        </v-flex>
                    </v-layout>
                </v-card-text>
                <v-divider></v-divider>
            </div>
        </div>

        <!-- ✅ Interrupt Dialog — pénalité uniquement MONEY_TIME_TARGET sans objectif atteint -->
        <v-dialog width="500px" v-model="interruptDialog">
            <v-card>
                <v-card-title class="headline orange darken-3 white--text">
                    <v-icon left dark>warning</v-icon>
                    Confirm Campaign Interruption
                </v-card-title>

                <v-card-text class="pa-4">
                    <!-- ✅ Pénalité UNIQUEMENT si MONEY_TIME_TARGET et objectif pas encore atteint -->
                    <template v-if="getCampaignType(campaign) === 'MONEY_TIME_TARGET' && !goalReachedEarly">
                        <!-- Montant fixe des frais récupéré depuis le back -->
                        <p v-if="interruptFee > 0">
                            A disruption fee of
                            <b class="orange--text">{{ interruptFee }} FCFA</b>
                            will be deducted from your saved amount.
                        </p>
                        <p v-else>
                            There is <b>no disruption fee</b> for your campaign.
                        </p>

                        <!-- Affichage du détail si des frais existent -->
                        <v-card flat color="orange lighten-5" class="pa-3 mb-3" v-if="campaign.saved_amount && interruptFee > 0">
                            <v-layout row wrap>
                                <v-flex xs6>
                                    <div class="caption">Disruption fee</div>
                                    <div class="title orange--text font-weight-bold">
                                        {{ interruptFee }} FCFA
                                    </div>
                                </v-flex>
                                <v-flex xs6>
                                    <div class="caption">You will receive</div>
                                    <div class="title teal--text font-weight-bold">
                                        {{ campaign.saved_amount - interruptFee }} FCFA
                                    </div>
                                </v-flex>
                            </v-layout>
                        </v-card>
                    </template>

                    <!-- Pas de pénalité pour les autres types -->
                    <template v-else>
                        <v-alert type="info" :value="true" color="blue lighten-4" class="mb-3">
                            <v-icon left color="blue darken-2">info</v-icon>
                            No penalty applies to this campaign type.
                            You will receive your full saved amount of
                            <b class="teal--text">{{ campaign.saved_amount }} FCFA</b>.
                        </v-alert>
                    </template>

                    <p><b>Note:</b> Once this interruption is confirmed, it takes <b>24–48 hrs</b> to be approved.</p>
                </v-card-text>

                <v-card-actions>
                    <v-btn color="orange darken-2" class="white--text" @click="setInterruptCampaign()" :loading="interruptingCampaign">
                        <v-icon left small>cancel</v-icon>
                        Interrupt
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="teal" flat @click="interruptDialog = false">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import axios from 'axios'
    import 'animate.css'

    export default {
        data() {
            return {
                campaign: [],
                campaignTransactions: [],
                campaignRequestedTransactions: [],
                businessTransactions: [],
                interruptDialog: false,
                interruptingCampaign: false,
                payment_ref: '',
                interrupt_fees: '',
                totalRequests: 0,
                interruptFee: null,
                maxRequests: 3,
            }
        },

        computed: {
            // ✅ True si objectif financier atteint AVANT la date de fin
            goalReachedEarly() {
                if (this.getCampaignType(this.campaign) !== 'MONEY_TIME_TARGET') return false
                const saved = Number(this.campaign.saved_amount) || 0
                const target = Number(this.campaign.target_amount) || 0
                const targetDate = this.campaign.target_date
                const moneyReached = saved >= target
                const timeNotReached = !this._isTargetDateReached(targetDate)
                return moneyReached && timeNotReached
            },

            // ✅ True si la durée est écoulée mais l'objectif financier non atteint
            durationReachedWithoutAmount() {
                if (this.getCampaignType(this.campaign) !== 'MONEY_TIME_TARGET') return false
                const saved = Number(this.campaign.saved_amount) || 0
                const target = Number(this.campaign.target_amount) || 0
                const moneyNotReached = saved < target
                const timeReached = this._isTargetDateReached(this.campaign.target_date)
                return timeReached && moneyNotReached
            }
        },

        mounted() {
            this.getCampaignDetail();
            this.getCampaignTransactions();
            this.getCampaignRequestedTransactions();
            this.getBusinessTransactions();

            document.title = "PettyCash | Campaign Detail Info."
            this.$store.commit('setCloseURL', "/savings/campaigns/active")

            const campaign_id = this.$route.params.campaign_id;
            if (campaign_id) {
                this.changeTelDial(campaign_id);
            }
        },

        methods: {
            // ✅ Calcul fiable du type depuis target_amount/target_date
            getCampaignType(campaign) {
                const hasAmount = !!campaign?.target_amount
                const hasDate   = !!campaign?.target_date
                if (hasAmount && hasDate) return 'MONEY_TIME_TARGET'
                if (hasAmount)            return 'MONEY_TARGET'
                if (hasDate)              return 'TIME_TARGET'
                return 'UNDEFINED_TARGET'
            },

            _isTargetDateReached(targetDateStr) {
                if (!targetDateStr || typeof targetDateStr !== 'string') return false
                const parts = targetDateStr.split('-').map(Number)
                if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return false

                const [year, month, day] = parts
                const target = new Date(year, month - 1, day)
                const today = new Date()
                target.setHours(0, 0, 0, 0)
                today.setHours(0, 0, 0, 0)
                return today.getTime() >= target.getTime()
            },

            async getBusinessTransactions() {
                this.$store.commit('setIsLoading', true)
                const campaign_id = this.$route.params.campaign_id
                try {
                    const response = await axios.get(`/api/v1/home/get/user/campaign/${campaign_id}/business_transactions/`)
                    this.businessTransactions = response.data
                } catch (error) {
                    if (error.response) {
                        this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                        this.$store.commit('setSnackBarColor', "red darken-4")
                        this.$store.commit('activateSnackBar', true)
                    }
                } finally {
                    this.$store.commit('setIsLoading', false)
                }
            },

            setInterruptCampaign() {
                this.interruptDialog = false
                this.$store.commit('setSnackBarMessage', '🚧 This feature is currently under development and will be available soon. Thank you for your patience!')
                this.$store.commit('setSnackBarColor', 'orange darken-3')
                this.$store.commit('activateSnackBar', true)
            },

            async getCampaignDetail() {
                this.$store.commit('setIsLoading', true)
                const campaign_id = this.$route.params.campaign_id
                await axios
                    .get('/api/v1/savings/get/user/campaign/detail/' + campaign_id + '/')
                    .then(response => { this.campaign = response.data
                    this.interruptFee = response.data.interrupt_fee ?? 0 })
                    .catch(error => {
                        if (error.response) {
                            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }
                    })
                this.$store.commit('setIsLoading', false)
            },

            async getCampaignTransactions() {
                this.$store.commit('setIsLoading', true)
                const campaign_id = this.$route.params.campaign_id
                await axios
                    .get('/api/v1/savings/get/user/campaign/' + campaign_id + '/transactions/')
                    .then(response => { this.campaignTransactions = response.data })
                    .catch(error => {
                        if (error.response) {
                            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }
                    })
                this.$store.commit('setIsLoading', false)
            },

            async getCampaignRequestedTransactions() {
                this.$store.commit('setIsLoading', true)
                const campaign_id = this.$route.params.campaign_id
                await axios
                    .get('/api/v1/savings/get/user/campaign/' + campaign_id + '/requested_transactions/')
                    .then(response => { this.campaignRequestedTransactions = response.data.sort((a, b) => b.id - a.id) })
                    .catch(error => {
                        if (error.response) {
                            this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }
                    })
                this.$store.commit('setIsLoading', false)
            },

            async changeTelDial() {
                const campaign_id = this.$route.params.campaign_id
                if (!campaign_id) return
                axios.get(`/api/v1/savings/total/${campaign_id}/`)
                    .then(response => {
                        this.totalRequests = response.data.total_requests
                        if (this.totalRequests >= this.maxRequests) {
                            this.$store.commit('setSnackBarMessage', "You have reached the limit of 3 requests per day.")
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                        }
                    })
                    .catch(error => { console.error("Error fetching total requests:", error) })
            }
        }
    }
</script>

<style scoped>
.slide-fade-enter-active { transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1); }
.slide-fade-leave-active  { transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); }
.slide-fade-enter, .slide-fade-leave-to { transform: translateX(400px); opacity: 0; }

* { scroll-behavior: smooth; }
</style>